import { ChartAssetData, PaneData } from '@toggle/chart';
import { Chip, Dropdown, SvgIconNames } from '@toggle/design-system';
import classnames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';

import { ResampleIntervals } from '~/types/resample.types';
import type { SeriesType } from '~/types/series.types';
import { isCommodity, isFX, isSinglePrice } from '~/utils/entity/chart-entity';
import { hasOHLC as hasOHLCData } from '~/utils/timeseries/time-series';

import styles from './ChartTypeSelector.module.scss';
import { ChartTypeSelectorItem } from './ChartTypeSelectorItem';
import {
  isChartTypeNotSupportedForCommodity,
  isChartTypeNotSupportedForFX,
  isResampleOneDayOrGreater,
} from './utils';

export interface ChartTypeSelectorProps {
  paneData?: PaneData[];
  selectedSeriesType?: SeriesType;
  disableMenuBar: boolean;
  onChange: (
    chartType: SeriesType,
    { snake }: { snake?: string; paneIndex?: number }
  ) => void;
  hasOHLC?: boolean;
  className?: string;
  defaultSnake?: string;
  selectedResample?: ResampleIntervals;
}

interface IndexedChartAsset extends ChartAssetData {
  paneIndex: number;
}

const availableCharts: SeriesType[] = ['line', 'candlestick', 'bars'];

export const chartTypeToIconName: Record<SeriesType, SvgIconNames> = {
  bars: 'Bars',
  candlestick: 'Candlestick',
  line: 'Line',
};

export const ChartTypeSelector = ({
  selectedSeriesType,
  paneData,
  onChange,
  disableMenuBar,
  hasOHLC = false,
  className,
  defaultSnake,
  selectedResample,
}: ChartTypeSelectorProps) => {
  const [showChartType, setShowChartType] = useState(false);
  const [chartDropdown, setChartDropdown] = useState<SeriesType>('line');

  const renderDropdownItem = (item: IndexedChartAsset) => {
    const series = item.seriesType ?? 'line';
    const { entity, paneIndex } = item;
    const isDropdownDisabled =
      isChartTypeNotSupportedForFX(entity, selectedResample, chartDropdown) ||
      isChartTypeNotSupportedForCommodity(
        entity,
        selectedResample,
        chartDropdown
      );
    const props = {
      entity,
      series,
      isChecked: series === chartDropdown,
      disabled: isDropdownDisabled,
    };
    return (
      <ChartTypeSelectorItem
        {...props}
        onClick={() =>
          onChange(series === chartDropdown ? 'line' : chartDropdown, {
            snake: item.entity.default_snake,
            paneIndex,
          })
        }
      />
    );
  };

  const onOutsideClick = () => {
    setShowChartType(false);
  };

  const dropDownItems: IndexedChartAsset[] = useMemo(
    () =>
      paneData?.flatMap(
        (pane, paneIndex) =>
          pane.chartAssetData
            .map(data =>
              isSinglePrice(data.entity)
                ? null
                : {
                    ...data,
                    paneIndex,
                  }
            )
            .filter(Boolean) as IndexedChartAsset[]
      ) ?? [],
    [paneData]
  );

  const activeSeriesTypes = useMemo(
    () =>
      dropDownItems?.reduce(
        (a, data) => {
          a[data?.seriesType ?? 'line'] += 1;
          return a;
        },
        { line: 0, candlestick: 0, bars: 0 } as Record<SeriesType, number>
      ),
    [dropDownItems]
  );

  const hasFXAsset = useMemo(() => {
    if (!paneData) {
      return false;
    }

    return paneData.some(pane =>
      pane.chartAssetData.some(data => isFX(data.entity))
    );
  }, [paneData]);

  const hasCommodityAsset = useMemo(() => {
    if (!paneData) {
      return false;
    }

    return paneData.some(pane =>
      pane.chartAssetData.some(data => isCommodity(data.entity))
    );
  }, [paneData]);

  const isResampleOneDayOrGreaterValue = useMemo(
    () => isResampleOneDayOrGreater(selectedResample),
    [selectedResample]
  );

  const computedHasOHLC = useMemo(() => {
    if (paneData && dropDownItems.length > 0) {
      return dropDownItems.some(item => hasOHLCData(item));
    }

    return hasOHLC;
  }, [paneData, dropDownItems, hasOHLC]);

  useEffect(() => {
    if (!paneData) {
      setShowChartType(false);
      setChartDropdown('line');
    }
  }, [paneData]);

  // Automatically switch to line chart for FX assets and commodities with non-intraday resample
  useEffect(() => {
    const shouldSwitchToLine =
      (hasFXAsset || hasCommodityAsset) && isResampleOneDayOrGreaterValue;

    if (!shouldSwitchToLine) {
      return;
    }

    // Use a small delay to ensure paneData has updated after resample change
    const timeoutId = setTimeout(() => {
      if (paneData) {
        // For charts with paneData, check both pane seriesType and selectedSeriesType
        paneData.forEach((pane, paneIndex) => {
          const paneHasFXAsset = pane.chartAssetData.some(
            data => isFX(data.entity) && !isSinglePrice(data.entity)
          );
          const paneHasCommodityAsset = pane.chartAssetData.some(
            data => isCommodity(data.entity) && !isSinglePrice(data.entity)
          );

          if (!paneHasFXAsset && !paneHasCommodityAsset) {
            return;
          }

          const currentSeriesType =
            paneIndex === 0 && selectedSeriesType
              ? selectedSeriesType
              : pane.seriesType ?? 'line';

          if (
            currentSeriesType !== 'line' &&
            (currentSeriesType === 'candlestick' ||
              currentSeriesType === 'bars')
          ) {
            onChange('line', { paneIndex });
            return;
          }

          pane.chartAssetData.forEach(data => {
            const assetSeriesType = data.seriesType ?? currentSeriesType;

            if (
              (isFX(data.entity) || isCommodity(data.entity)) &&
              (assetSeriesType === 'candlestick' ||
                assetSeriesType === 'bars') &&
              !isSinglePrice(data.entity)
            ) {
              onChange('line', {
                snake: data.entity.default_snake,
                paneIndex,
              });
            }
          });
        });
      } else if (
        selectedSeriesType === 'candlestick' ||
        selectedSeriesType === 'bars'
      ) {
        onChange('line', { snake: defaultSnake });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    hasFXAsset,
    hasCommodityAsset,
    isResampleOneDayOrGreaterValue,
    paneData,
    selectedSeriesType,
    onChange,
    defaultSnake,
  ]);

  return (
    <div
      className={classnames(styles.chartTypeSelectorRoot, className)}
      data-testid="chart-type-selector"
    >
      {availableCharts.map((chartType, index) => {
        const disableChip =
          disableMenuBar ||
          (['bars', 'candlestick'].includes(chartType) && !computedHasOHLC);

        return (
          <React.Fragment key={`selector-type-${chartType}`}>
            {paneData ? (
              dropDownItems.length === 1 ? (
                <Chip
                  className={styles.styledChip}
                  data-testid={`${chartType}-chart-button`}
                  key={chartType}
                  type="choice"
                  leftIcon={chartTypeToIconName[chartType]}
                  isActive={
                    chartType === (dropDownItems[0].seriesType ?? 'line')
                  }
                  disabled={
                    disableChip ||
                    isChartTypeNotSupportedForFX(
                      dropDownItems[0].entity,
                      selectedResample,
                      chartType
                    ) ||
                    isChartTypeNotSupportedForCommodity(
                      dropDownItems[0].entity,
                      selectedResample,
                      chartType
                    )
                  }
                  onClick={() => {
                    const singleAsset = dropDownItems[0];
                    onChange(chartType, {
                      snake: singleAsset.entity.default_snake,
                      paneIndex: singleAsset.paneIndex,
                    });
                  }}
                />
              ) : (
                <Dropdown
                  className={styles.chartTypeDropdown}
                  data-testid={`${chartType}-dropdown`}
                  key={`${chartType}-${index}`}
                  dropdownItems={dropDownItems}
                  renderDropdownItem={e => renderDropdownItem(e)}
                  enableFloating
                  inPortal
                  shouldShowItems={chartDropdown === chartType && showChartType}
                  shouldCloseOnSelect={false}
                  placement="bottom-start"
                  shouldSetActiveOnSelect={false}
                  onOutsideClick={onOutsideClick}
                >
                  <Chip
                    className={styles.styledChip}
                    data-testid={`${chartType}-chart-button`}
                    key={chartType}
                    type="choice"
                    leftIcon={chartTypeToIconName[chartType]}
                    isActive={
                      activeSeriesTypes[chartType] === dropDownItems.length
                    }
                    disabled={disableChip}
                    onClick={() => {
                      setChartDropdown(chartType);
                      setShowChartType(prev =>
                        chartDropdown === chartType ? !prev : true
                      );
                    }}
                  />
                </Dropdown>
              )
            ) : (
              <Chip
                className={styles.styledChip}
                data-testid={`${chartType}-chip-button`}
                key={chartType}
                type="choice"
                leftIcon={chartTypeToIconName[chartType]}
                isActive={chartType === selectedSeriesType}
                disabled={disableChip}
                onClick={() => {
                  onChange(chartType, { snake: defaultSnake });
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
